import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Modal, Text } from '@mantine/core';

import { useGetForm, usePatchForm } from 'api/forms';
import { Form, TextInputPassword } from 'components/shared';
import { useShowNotification } from 'hooks';

import { useFormEditContext } from '../../context';

type Props = {
  opened: boolean;
  onClose: () => void;
};

type FormData = {
  accessCode: string;
};

export const PasswordModal = ({ opened, onClose }: Props) => {
  const {
    form: { id, accessCode },
  } = useFormEditContext();
  const {
    register,
    handleSubmit,
    watch,
    formState: { isDirty, isSubmitting },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      accessCode: accessCode ?? '',
    },
  });

  const { refetch: refetchForm } = useGetForm({ id });
  const { mutateAsync: patchForm } = usePatchForm({ id });

  const showNotification = useShowNotification();
  const { t } = useTranslation('forms');

  const onSubmit = async ({ accessCode: newAccessCode }: FormData) => {
    try {
      await patchForm({ accessCode: newAccessCode });
      showNotification({
        message: t('editView.sidebar.sharing.passwordModal.updateSuccessToast'),
        color: 'green',
      });
      onClose();
      refetchForm();
    } catch (error) {
      showNotification({
        message: t('editView.sidebar.sharing.passwordModal.updateErrorToast'),
        color: 'red',
      });
    }
  };

  const copyToClipboard = () => {
    const accessCodeValue = watch('accessCode');
    navigator.clipboard.writeText(accessCodeValue ?? '');
    showNotification({
      message: t('editView.sidebar.sharing.passwordModal.copySuccessToast'),
      color: 'green',
    });
  };

  useEffect(() => {
    reset({ accessCode: accessCode ?? '' });
  }, [accessCode]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('editView.sidebar.sharing.passwordModal.title')}
      size="md"
      centered
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" gap="lg">
          <Text size="md">
            {t('editView.sidebar.sharing.passwordModal.description')}
          </Text>
          <Flex gap="sm">
            <TextInputPassword
              placeholder={t(
                'editView.sidebar.sharing.passwordModal.inputPlaceholder',
              )}
              type="password"
              withErrorWrapperAlwaysEnabled={false}
              isClearableButtonVisible={false}
              sx={{ flex: 1 }}
              {...register('accessCode')}
            />
            <Button onClick={() => copyToClipboard()}>
              {t('editView.sidebar.sharing.passwordModal.copyButton')}
            </Button>
          </Flex>
        </Flex>
        <Flex
          justify="flex-end"
          gap="md"
          sx={theme => ({ marginTop: theme.other.spacing(3) })}
        >
          <Button variant="default" onClick={onClose}>
            {t('editView.sidebar.sharing.passwordModal.cancelButton')}
          </Button>
          <Button type="submit" disabled={!isDirty} loading={isSubmitting}>
            {t('editView.sidebar.sharing.passwordModal.saveButton')}
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};
